import "./DoneProgress.css";

import { type FC, useContext } from "react";

import { t } from "i18n/i18n";
import { Card } from "mds/components/Card";
import { DocViewContext } from "providers/DocViewProvider";
import { useAppSelector } from "store/index";
import {
  pageAccessDataSelectors,
  pageSelectors,
  selectPageGroupsWithUsersByPageId,
} from "store/selectors";
import { selectCountUsersWhoCanParticipateInSection } from "store/selectors/permissions";
import { useObjectSelector } from "store/store-hooks";
import { isGroupPage } from "utils/page-utils";

export const DoneProgress: FC<{ className?: string }> = ({ className }) => {
  const { pageId } = useContext(DocViewContext);
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    ["due_at", "interaction_mode"],
  );
  const nStudents = useAppSelector(selectCountUsersWhoCanParticipateInSection);
  const groups = useAppSelector((s) => selectPageGroupsWithUsersByPageId(s, pageId));
  const pageAccessData = useAppSelector((s) => pageAccessDataSelectors.filterByPageId(s, pageId));

  // After a page is due, editing is either disabled (making mark done irrelevant),
  // or late submissions are allowed, in which case the submit button is shown instead
  // and the mark done button is hidden.
  if (!page || (page.due_at && new Date(page.due_at) < new Date())) {
    return null;
  }

  const isGrouped = isGroupPage(page);

  const doneCount = pageAccessData.filter((accessData) => accessData.completed_at).length;

  const totalCount = isGrouped
    ? groups.filter((g) => g.page_group_users.length > 0).length
    : nStudents;

  return (
    <Card className="body-s-bold mb-4 w-full" kind="blue">
      <div className={className} title={t("presentation_aggregates.tooltip")}>
        <label className="flex flex-col gap-2 self-start">
          <div>
            {t(
              isGrouped
                ? "presentation_aggregates.done_groups"
                : "presentation_aggregates.done_students",
              { doneCount, count: totalCount },
            )}
          </div>

          <progress className="done-progress w-full" max={totalCount} value={doneCount} />
        </label>
      </div>
    </Card>
  );
};
